import { guardNever } from "../errors.js";

export const DBT_ACCESS_URL_MULTI_TENANT_REGEXP =
  /^(?<semanticLayerOrMetadata>(?:semantic-layer|metadata)\.)?(?<baseUrl>(?:cloud\.get|(emea|au)\.)dbt\.com)$/;
export const DBT_ACCESS_URL_MULTI_CELL_REGEXP =
  /^(?<accountPrefix>.+?)\.(?<semanticLayerOrMetadata>(?:semantic-layer|metadata)\.)?(?<baseUrl>(?:us1|us2|us3|eu1|eu2|eu3|eu4|au1|jp1)\.dbt\.com)$/;
export const DBT_ACCESS_URL_SINGLE_TENANT_REGEXP =
  /^(?<semanticLayerOrMetadata>(?:semantic-layer|metadata)\.)?(?<baseUrl>(?:cloud\.[a-zA-Z0-9-]+)\.getdbt\.com)$/;

export type AccessUrlParams = {
  accountPrefix?: string;
  baseUrl: string;
  semanticLayerOrMetadata?: string;
};

/**
 * From a provided access URL, determine the account prefix (if multi-cell),
 * the base URL (e.g. cloud.getdbt.com, us1.dbt.com, etc.), and, if provided
 * in the URL, the text determining if this is a semantic-layer or metadata
 * URL (as opposed to the raw access URL)
 *
 * Examples:
 * `abc.us1.dbt.com` -> { accountPrefix: abc, baseUrl: us1.dbt.com}
 * `cloud.getdbt.com` -> { baseUrl: cloud.getdbt.com }
 * `semantic-layer.cloud.getdbt.com` -> { baseUrl: cloud.getdbt.com, semanticLayerOrMetadata: 'semantic-layer.' }
 *
 * @param providedUrl The access URL that the customer uses to access dbt.
 * @returns The account prefix (if multi-cell), the base URL, and whether this was a semantic-layer or metadata URL
 */
export function resolveAccessUrlInfo(providedUrl: string): AccessUrlParams {
  const match =
    providedUrl.match(DBT_ACCESS_URL_MULTI_TENANT_REGEXP) ||
    providedUrl.match(DBT_ACCESS_URL_MULTI_CELL_REGEXP) ||
    providedUrl.match(DBT_ACCESS_URL_SINGLE_TENANT_REGEXP);
  if (match?.groups != null && "baseUrl" in match.groups) {
    return {
      accountPrefix: match.groups.accountPrefix,
      baseUrl: match.groups.baseUrl,
      semanticLayerOrMetadata: match.groups.semanticLayerOrMetadata,
    };
  } else {
    throw new Error(
      `${providedUrl} is not a known valid dbt multi-tenant, multi-cell, or single-tenant access URL. Not all customised access URLs are supported.`,
    );
  }
}

export function semanticLayerUrlFromAccessUrl(providedUrl: string): string {
  return urlFromAccessUrl(providedUrl, "SEMANTIC_LAYER");
}

export function metadataUrlFromAccessUrl(providedUrl: string): string {
  return urlFromAccessUrl(providedUrl, "METADATA");
}

export function webUrlFromAccessUrl(providedUrl: string): string {
  return urlFromAccessUrl(providedUrl, "WEB");
}

function urlFromAccessUrl(
  providedUrl: string,
  requiredType: "METADATA" | "SEMANTIC_LAYER" | "WEB",
): string {
  const { accountPrefix, baseUrl } = resolveAccessUrlInfo(providedUrl);

  let urlPart = "";
  if (requiredType === "METADATA") {
    urlPart = "metadata.";
  } else if (requiredType === "SEMANTIC_LAYER") {
    urlPart = "semantic-layer.";
  } else if (requiredType !== "WEB") {
    guardNever(requiredType, requiredType);
  }

  // accountPrefix != null ? multi-cell : multi-tenant
  return accountPrefix != null
    ? `${accountPrefix}.${urlPart}${baseUrl}`
    : `${urlPart}${baseUrl}`;
}
